/**
 * Engage i18n provider — mirrors SPECTRE's `I18nProvider` shape so the file
 * is a clean drop-in when this app folds into SPECTRE. The public API
 * (`useI18n()` → `{ lang, setLang, t }`) is unchanged; the translation data
 * lives in `./translations.ts` (key-major: key → { en, pt, nl }) so the
 * dictionary can grow without bloating this file.
 */

import { createContext, useCallback, useContext, useState, type ReactNode } from "react";

import { translations, type Lang } from "./translations";

export type { Lang };

interface I18nContextType {
  lang: Lang;
  setLang: (lang: Lang) => void;
  t: (key: string) => string;
}

const I18nContext = createContext<I18nContextType>({
  lang: "en",
  setLang: () => {},
  t: (key) => key,
});

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(() => {
    const saved = localStorage.getItem("paybyrd_lang");
    return saved === "pt" || saved === "nl" ? saved : "en";
  });

  const setLang = useCallback((l: Lang) => {
    setLangState(l);
    localStorage.setItem("paybyrd_lang", l);
  }, []);

  // Resolve to the active language, fall back to English, then to the raw
  // key so a missing translation is visible rather than blank.
  const t = useCallback(
    (key: string) => translations[key]?.[lang] || translations[key]?.en || key,
    [lang],
  );

  return <I18nContext.Provider value={{ lang, setLang, t }}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  return useContext(I18nContext);
}

export default I18nContext;
